import React, { useState } from 'react'
import { authService } from '../../authService'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Tune as TuneIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
  CheckCircle as CheckCircleIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Business as BusinessIcon,
  Help as HelpIcon
} from '@mui/icons-material';

const SettingsModal = ({ user, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  // Preferences state
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    browserNotifications: false,
    theme: 'light'
  })

  if (!isOpen) return null

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSuccess('');
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }))
    setError('')
  }

  const handleChangePassword = async () => {
    setError('')
    setSuccess('')

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('All password fields are required')
      return
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long')
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match')
      return
    }

    setLoading(true)
    try {
      const result = await authService.changePassword(
        user.userid,
        passwordData.currentPassword,
        passwordData.newPassword
      )

      if (result.success) {
        setSuccess('Password changed successfully!')
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(result.error || 'Failed to change password')
      }
    } catch (err) {
      setError('An error occurred while changing password')
    }
    setLoading(false)
  }

  const handleSavePreferences = () => {
    setSuccess('Preferences saved successfully!')
    setTimeout(() => setSuccess(''), 2000)
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">Settings</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs">
          <Tab icon={<SecurityIcon />} iconPosition="start" label="Security" />
          <Tab icon={<TuneIcon />} iconPosition="start" label="Preferences" />
          <Tab icon={<InfoIcon />} iconPosition="start" label="About" />
        </Tabs>
      </Box>

      <DialogContent dividers>
        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Security Tab */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <VpnKeyIcon color="primary" />
              <Typography variant="h6">Change Password</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Keep your account secure by using a strong password
            </Typography>

            <Box sx={{ bgcolor: 'background.default', p: 3, borderRadius: 2 }}>
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                margin="normal"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                margin="normal"
                size="small"
                helperText="Password must be at least 6 characters long"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKeyIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                margin="normal"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CheckCircleIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  Update Password
                </Button>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold">Security Tips:</Typography>
              <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                <li>Use a unique password for this account</li>
                <li>Include uppercase, lowercase, numbers, and symbols</li>
                <li>Avoid using personal information in your password</li>
                <li>Change your password regularly</li>
              </ul>
            </Alert>
          </Box>
        )}

        {/* Preferences Tab */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TuneIcon color="primary" />
              <Typography variant="h6">Application Preferences</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Customize your experience
            </Typography>

            <Box sx={{ bgcolor: 'background.default', p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsIcon fontSize="small" /> Notifications
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.emailNotifications}
                      onChange={(e) => setPreferences(prev => ({ ...prev, emailNotifications: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Enable email notifications"
                />
                <Box />
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.browserNotifications}
                      onChange={(e) => setPreferences(prev => ({ ...prev, browserNotifications: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label="Enable browser notifications"
                />
              </Box>

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaletteIcon fontSize="small" /> Theme (Coming Soon)
              </Typography>

              <FormControl fullWidth size="small" disabled sx={{ mb: 1 }}>
                <InputLabel>Theme Mode</InputLabel>
                <Select
                  value={preferences.theme}
                  onChange={(e) => setPreferences(prev => ({ ...prev, theme: e.target.value }))}
                  label="Theme Mode"
                >
                  <MenuItem value="light">Light Mode</MenuItem>
                  <MenuItem value="dark">Dark Mode</MenuItem>
                  <MenuItem value="auto">Auto (System)</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Theme customization will be available in a future update
              </Typography>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSavePreferences}
                  startIcon={<SaveIcon />}
                >
                  Save Preferences
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* About Tab */}
        {activeTab === 2 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InfoIcon color="primary" />
              <Typography variant="h6">About This System</Typography>
            </Box>
            
            <Box sx={{ bgcolor: 'background.default', p: 4, borderRadius: 2, textAlign: 'center', mb: 3 }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
                color: 'white',
                mb: 2,
                margin: '0 auto'
              }}>
                <BusinessIcon fontSize="large" />
              </Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Water Billing System
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                LGU Concepcion, Romblon
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell variant="head"><strong>Version</strong></TableCell>
                    <TableCell>2.0.0</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head"><strong>Build Date</strong></TableCell>
                    <TableCell>October 2025</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head"><strong>Technology</strong></TableCell>
                    <TableCell>React.js + Supabase</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head"><strong>Database</strong></TableCell>
                    <TableCell>PostgreSQL (Supabase)</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell variant="head"><strong>UI Framework</strong></TableCell>
                    <TableCell>Material UI</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mt: 3 }} icon={<HelpIcon />}>
              <Typography variant="subtitle2" fontWeight="bold">Need Help?</Typography>
              <Typography variant="body2">
                Contact your system administrator or IT support team for assistance with this application.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SettingsModal
